// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, paginationQuerySchema, uuidSchema } from '../common.js';
/**
 * HR attendance workspace (HR portal Prompt 6a): calendar register, add / edit record with a policy preview, bulk status,
 * punch timeline, monthly summary and the unmatched-punch triage. Every write is a correction (raw stays immutable) or a
 * device identity mapping; nothing here patches a daily record directly.
 */
const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');
/** Statuses HR may set by hand (a SET_STATUS correction). PENDING / NOT_JOINED / EXITED / MISSING_PUNCH stay engine-derived. */
export const MANUAL_ATTENDANCE_STATUSES = ['PRESENT', 'ABSENT', 'HALF_DAY', 'LEAVE', 'HOLIDAY', 'WEEKLY_OFF'];
/** Auto = the engine decided the status; Manual = the day carries an applied SET_STATUS correction. */
export const STATUS_SOURCES = ['AUTO', 'MANUAL'];
const outAfterIn = (v, ctx) => {
    if (v.inAt && v.outAt && Date.parse(v.outAt) <= Date.parse(v.inAt))
        ctx.addIssue({ code: 'custom', path: ['outAt'], message: 'Check-out must be after check-in' });
    if (v.removeIn && v.inAt)
        ctx.addIssue({ code: 'custom', path: ['removeIn'], message: 'Either remove the check-in or move it, not both' });
    if (v.removeOut && v.outAt)
        ctx.addIssue({ code: 'custom', path: ['removeOut'], message: 'Either remove the check-out or move it, not both' });
};
// ---- preview (no writes) ------------------------------------------------------------------------------------------------
/** POST /orgs/:orgId/attendance/preview — the policy-derived status/hours of a day with the proposed check-in / check-out. */
export const attendancePreviewInputSchema = z.object({
    employeeId: uuidSchema,
    date: isoDateSchema,
    /** Proposed check-in (UTC instant); absent or null = keep the day's current check-in. */
    inAt: isoDateTimeSchema.nullish(),
    /** Proposed check-out (UTC instant); absent or null = keep the day's current check-out. */
    outAt: isoDateTimeSchema.nullish(),
    /** Remove the day's check-in (the first IN the engine attributed): what clearing the check-in time means. */
    removeIn: z.boolean().optional(),
    /** Remove the day's check-out (the last OUT the engine attributed): what clearing the check-out time means. */
    removeOut: z.boolean().optional(),
}).superRefine(outAfterIn);
// ---- record edit (Add / Edit record dialog) -----------------------------------------------------------------------------
/** POST /orgs/:orgId/attendance/record-edits — files ADD_PUNCH / EDIT_PUNCH / REMOVE_PUNCH / SET_STATUS corrections through the engine path. */
export const attendanceRecordEditSchema = z.object({
    employeeId: uuidSchema,
    date: isoDateSchema,
    inAt: isoDateTimeSchema.nullish(),
    outAt: isoDateTimeSchema.nullish(),
    /** Remove the day's check-in / check-out (the time was cleared in the dialog): files a REMOVE_PUNCH correction for it. */
    removeIn: z.boolean().optional(),
    removeOut: z.boolean().optional(),
    /** Manual status override; absent = keep the policy-derived status. */
    status: z.enum(MANUAL_ATTENDANCE_STATUSES).optional(),
    reason: z.string().trim().min(3).max(1000),
}).superRefine((v, ctx) => {
    outAfterIn(v, ctx);
    if (!v.inAt && !v.outAt && !v.removeIn && !v.removeOut && !v.status)
        ctx.addIssue({ code: 'custom', path: ['status'], message: 'Set a check-in, a check-out or a status' });
});
// ---- bulk status ------------------------------------------------------------------------------------------------------------
export const BULK_STATUS_MAX_ITEMS = 200;
/** POST /orgs/:orgId/attendance/bulk-status — one SET_STATUS correction per row, one reason. */
export const bulkAttendanceStatusSchema = z.object({
    items: z.array(z.object({ employeeId: uuidSchema, date: isoDateSchema })).min(1).max(BULK_STATUS_MAX_ITEMS),
    status: z.enum(MANUAL_ATTENDANCE_STATUSES),
    reason: z.string().trim().min(3).max(1000),
});
// ---- Auto / Manual status source --------------------------------------------------------------------------------------------
/**
 * GET /orgs/:orgId/attendance/manual-statuses — the days in a range whose status is a manual override (an applied SET_STATUS
 * correction, latest wins), so the register can badge `Manual` next to the engine's `Auto` without widening the daily DTO.
 */
export const manualStatusesQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    branchId: uuidSchema.optional(),
    employeeId: uuidSchema.optional(),
}).refine((v) => v.to >= v.from, { message: 'to must be on/after from', path: ['to'] })
    .refine((v) => Date.parse(`${v.to}T00:00:00Z`) - Date.parse(`${v.from}T00:00:00Z`) <= 62 * 86_400_000, { message: 'At most 62 days', path: ['to'] });
// ---- calendar ------------------------------------------------------------------------------------------------------------
export const attendanceCalendarQuerySchema = z.object({
    month: monthSchema,
    employeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(12),
});
// ---- monthly summary --------------------------------------------------------------------------------------------------------
export const attendanceSummaryQuerySchema = z.object({
    month: monthSchema,
    employeeId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** The summary's filters without paging (the set an export covers). */
export const attendanceSummaryFiltersSchema = attendanceSummaryQuerySchema.omit({ page: true, pageSize: true });
/**
 * POST /orgs/:orgId/attendance/summary/export — the summary of the filtered set as a FILE, generated by the worker like every
 * other report (report type `monthly_summary`, HR portal Prompt 6a review — defect 10): 202 with the report request id; the
 * file is downloaded from the Reports page (report.export, owner-only signed URL).
 */
export const attendanceSummaryExportSchema = attendanceSummaryFiltersSchema.extend({
    format: z.enum(['csv', 'xlsx', 'pdf']).default('csv'),
    /** Required when the organisation asks for a reason on exports (Settings → Security). */
    reason: z.string().trim().max(500).optional(),
});
/** Rows one export may hold; above it the export asks for a branch / department filter. */
export const ATTENDANCE_SUMMARY_EXPORT_MAX_ROWS = 10_000;
// ---- punch timeline -----------------------------------------------------------------------------------------------------------
export const attendanceTimelineQuerySchema = z.object({ employeeId: uuidSchema, date: isoDateSchema });
// ---- unmatched punch triage --------------------------------------------------------------------------------------------------
export const UNMATCHED_STATUSES = ['unmatched', 'ignored'];
export const unmatchedPunchesQuerySchema = paginationQuerySchema.extend({
    status: z.enum(UNMATCHED_STATUSES).default('unmatched'),
    deviceId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    search: z.string().trim().max(64).optional(),
});
/**
 * Devices whose punches are NOT attributed through the device identity mapping (HR portal Prompt 6a review — defect 4), so an
 * Assign could only write a mapping the normaliser never reads: the Flowza Finance connector resolves by employee number
 * (fix the number in FlowZa Time or in Finance), the self-service device by the member's linked employee.
 */
export const UNMATCHED_ASSIGN_BLOCKED_PROVIDERS = {
    flowza_finance: 'CONNECTOR_RESOLVES_BY_EMPLOYEE_NUMBER',
    self_service: 'SELF_SERVICE_RESOLVES_BY_MEMBERSHIP',
};
/** Why Assign is refused for a device's provider, or null when the device identity mapping decides. */
export function unmatchedAssignBlockedReason(providerKey) {
    return UNMATCHED_ASSIGN_BLOCKED_PROVIDERS[providerKey] ?? null;
}
const deviceUserSchema = z.object({ deviceId: uuidSchema, deviceEmployeeId: z.string().trim().min(1).max(64) });
export const unmatchedAssignSchema = deviceUserSchema.extend({ employeeId: uuidSchema });
export const unmatchedIgnoreSchema = deviceUserSchema.extend({ reason: z.string().trim().min(3).max(500) });
export const unmatchedRestoreSchema = deviceUserSchema;
//# sourceMappingURL=hr-workspace.js.map